/**
 * POS Menu Grid Component (Kiosk-Style)
 * Features:
 * - Search bar at the top
 * - Category text buttons in 2-3 rows
 * - Big icon buttons for menu items like kiosk
 * - Full dark/light mode support
 */
import { useMemo } from 'react';
import { 
  Search, Coffee, GlassWater, Apple, UtensilsCrossed, 
  Croissant, CakeSlice, Candy, Milk, IceCream, Grid3X3
} from 'lucide-react';
import { Input } from '@/components/ui/input';

// Category icons mapping
const CATEGORY_ICONS = {
  'coffee': Coffee,
  'iced-drinks': GlassWater,
  'smoothies-blends': Apple,
  'fresh-mixed-juice': GlassWater,
  'breakfast': Croissant,
  'lunch': UtensilsCrossed,
  'milk-shakes': Milk,
  'desserts': CakeSlice,
  'sweets': Candy,
  'ice-cream': IceCream,
  'all': Grid3X3
};

// Category colors for dark mode
const CATEGORY_COLORS = {
  'coffee': 'bg-amber-700 hover:bg-amber-600 dark:bg-amber-800 dark:hover:bg-amber-700',
  'iced-drinks': 'bg-cyan-600 hover:bg-cyan-500 dark:bg-cyan-700 dark:hover:bg-cyan-600',
  'smoothies-blends': 'bg-green-600 hover:bg-green-500 dark:bg-green-700 dark:hover:bg-green-600',
  'fresh-mixed-juice': 'bg-orange-500 hover:bg-orange-400 dark:bg-orange-600 dark:hover:bg-orange-500',
  'breakfast': 'bg-yellow-600 hover:bg-yellow-500 dark:bg-yellow-700 dark:hover:bg-yellow-600',
  'lunch': 'bg-red-600 hover:bg-red-500 dark:bg-red-700 dark:hover:bg-red-600',
  'milk-shakes': 'bg-pink-500 hover:bg-pink-400 dark:bg-pink-600 dark:hover:bg-pink-500',
  'desserts': 'bg-purple-600 hover:bg-purple-500 dark:bg-purple-700 dark:hover:bg-purple-600',
  'sweets': 'bg-rose-500 hover:bg-rose-400 dark:bg-rose-600 dark:hover:bg-rose-500',
  'ice-cream': 'bg-indigo-500 hover:bg-indigo-400 dark:bg-indigo-600 dark:hover:bg-indigo-500'
};

// Item colors based on category
const ITEM_COLORS = {
  'coffee': 'bg-amber-600 hover:bg-amber-500 dark:bg-amber-700 dark:hover:bg-amber-600',
  'iced-drinks': 'bg-cyan-600 hover:bg-cyan-500 dark:bg-cyan-700 dark:hover:bg-cyan-600',
  'smoothies-blends': 'bg-green-600 hover:bg-green-500 dark:bg-green-700 dark:hover:bg-green-600',
  'fresh-mixed-juice': 'bg-orange-500 hover:bg-orange-400 dark:bg-orange-600 dark:hover:bg-orange-500',
  'breakfast': 'bg-yellow-600 hover:bg-yellow-500 dark:bg-yellow-700 dark:hover:bg-yellow-600',
  'lunch': 'bg-red-600 hover:bg-red-500 dark:bg-red-700 dark:hover:bg-red-600',
  'milk-shakes': 'bg-pink-500 hover:bg-pink-400 dark:bg-pink-600 dark:hover:bg-pink-500',
  'desserts': 'bg-purple-600 hover:bg-purple-500 dark:bg-purple-700 dark:hover:bg-purple-600',
  'sweets': 'bg-rose-500 hover:bg-rose-400 dark:bg-rose-600 dark:hover:bg-rose-500',
  'ice-cream': 'bg-indigo-500 hover:bg-indigo-400 dark:bg-indigo-600 dark:hover:bg-indigo-500'
};

export default function POSMenuGrid({ 
  menuItems, 
  categories, 
  activeCategory, 
  setActiveCategory,
  onItemClick,
  search,
  setSearch
}) {
  // Filter items by category and search
  const filteredItems = useMemo(() => {
    return menuItems.filter(item => {
      const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
      const matchesSearch = !search || 
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description?.toLowerCase().includes(search.toLowerCase());
      return matchesCategory && matchesSearch && item.is_available;
    });
  }, [menuItems, activeCategory, search]);

  // Get category icon
  const getCategoryIcon = (categoryId) => {
    const normalizedId = categoryId?.toLowerCase().replace(/\s+/g, '-');
    return CATEGORY_ICONS[normalizedId] || Coffee;
  };

  // Get item color based on category
  const getItemColor = (category) => {
    const normalizedCat = category?.toLowerCase().replace(/\s+/g, '-');
    return ITEM_COLORS[normalizedCat] || 'bg-teal-600 hover:bg-teal-500 dark:bg-teal-700 dark:hover:bg-teal-600';
  };

  // Get category color
  const getCategoryColor = (categoryId) => {
    const normalizedId = categoryId?.toLowerCase().replace(/\s+/g, '-');
    return CATEGORY_COLORS[normalizedId] || 'bg-gray-600 hover:bg-gray-500 dark:bg-gray-700 dark:hover:bg-gray-600';
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-gray-900">
      {/* Search at the very top */}
      <div className="p-4 lg:p-5 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <Search className="absolute left-3 lg:left-4 top-1/2 -translate-y-1/2 w-5 h-5 lg:w-6 lg:h-6 text-gray-400" />
          <Input
            type="text"
            placeholder="Search for an item..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 lg:pl-12 h-12 lg:h-14 text-lg lg:text-xl bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
            data-testid="pos-menu-search"
          />
        </div>
      </div>

      {/* Category Text Buttons - 2-3 rows */}
      <div className="p-3 lg:p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-2 lg:gap-3">
          {/* All button */}
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-4 py-3 lg:px-5 lg:py-3.5 rounded-xl text-sm lg:text-base font-semibold transition-all flex items-center gap-2 ${
              activeCategory === 'all'
                ? 'bg-teal-600 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            data-testid="pos-category-all"
          >
            <Grid3X3 className="w-4 h-4 lg:w-5 lg:h-5" />
            All Items
          </button>
          
          {/* Category buttons */}
          {categories.filter(c => c.is_active !== false).map(category => {
            const IconComponent = getCategoryIcon(category.id);
            const isActive = activeCategory === category.id;
            
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-3 lg:px-5 lg:py-3.5 rounded-xl text-sm lg:text-base font-semibold transition-all flex items-center gap-2 ${
                  isActive
                    ? `${getCategoryColor(category.id)} text-white shadow-md`
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                data-testid={`pos-category-${category.id}`}
              >
                <IconComponent className="w-4 h-4 lg:w-5 lg:h-5" />
                {category.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Menu Items Grid - Kiosk-style big buttons */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        {/* Special Bula Coffee Pass Button - Always shown at the top */}
        <div className="mb-6">
          <button
            onClick={() => onItemClick({
              id: 'bula-coffee-pass-extended',
              name: 'Bula Coffee Pass - Extended',
              price: 95,
              description: '365 days of FREE daily coffee (7-10am + 1-3:30pm). $95 VAT-inclusive.',
              category: 'membership',
              is_coffee_pass: true,
              plan_type: 'extended',
              skip_customization: true
            })}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 dark:from-amber-600 dark:to-amber-700 dark:hover:from-amber-500 dark:hover:to-amber-600 rounded-2xl p-6 lg:p-8 flex items-center justify-between text-white shadow-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] border-2 border-amber-400 dark:border-amber-500"
            data-testid="pos-bula-coffee-pass-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-xl bg-white/20 flex items-center justify-center">
                <Coffee className="w-10 h-10 lg:w-12 lg:h-12 text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-xl lg:text-2xl font-bold">Bula Coffee Pass</h3>
                <p className="text-amber-100 text-sm lg:text-base">365 days FREE daily coffee (7-10am + 1-3:30pm)</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl lg:text-4xl font-bold">$95</div>
              <div className="text-amber-200 text-sm lg:text-base">VAT inclusive</div>
            </div>
          </button>
        </div>

        {filteredItems.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 lg:gap-5">
            {filteredItems.map(item => {
              const IconComponent = getCategoryIcon(item.category);
              const colorClass = getItemColor(item.category);
              
              return (
                <button
                  key={item.id}
                  onClick={() => onItemClick(item)}
                  className={`${colorClass} rounded-2xl p-4 lg:p-5 flex flex-col items-center justify-center text-center transition-all transform hover:scale-105 active:scale-95 shadow-lg min-h-[160px] lg:min-h-[180px]`}
                  data-testid={`pos-item-${item.id}`}
                >
                  {/* Item Image or Icon */}
                  {item.image ? (
                    <div className="w-18 h-18 lg:w-24 lg:h-24 rounded-xl overflow-hidden mb-2 lg:mb-3 bg-white/20">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="w-18 h-18 lg:w-24 lg:h-24 rounded-xl mb-2 lg:mb-3 bg-white/20 flex items-center justify-center">
                      <IconComponent className="w-12 h-12 lg:w-14 lg:h-14 text-white" />
                    </div>
                  )}
                  
                  {/* Item Name */}
                  <h4 className="font-bold text-white text-sm lg:text-base leading-tight mb-1 lg:mb-2 line-clamp-2">
                    {item.name}
                  </h4>
                  
                  {/* Price - Always show base item.price, not size[0] which might be Large */}
                  <p className="text-white/90 font-semibold text-base lg:text-lg">
                    ${(item.price || item.sizes?.[0]?.price || 0).toFixed(2)}
                    {item.sizes?.length > 1 && <span className="text-white/70">+</span>}
                  </p>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">No items found</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Try a different search or category</p>
          </div>
        )}
      </div>
    </div>
  );
}
